import React from "react";
import { NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  BarChart3,
  Settings,
  Bell,
  ShieldCheck,
  LogOut,
  Power,
  Globe,
  Wallet,
  FileText,
  Megaphone,
  Clock,
  Copy,
  AlertCircle,
  Mail,
  X,
  Sparkles,
  Link2,
} from "lucide-react";
import type { Admin } from "./types";

interface NavItem {
  path: string;
  label: string;
  icon: React.ReactNode;
}

interface NavSection {
  section: string;
  items: NavItem[];
}

interface AdminSidebarProps {
  admin: Admin;
  onLogout: () => void;
  mobileOpen: boolean;
  onMobileClose: () => void;
}

function AdminSidebar({
  admin,
  onLogout,
  mobileOpen,
  onMobileClose,
}: AdminSidebarProps) {
  const navItems: NavSection[] = [
    {
      section: "Main",
      items: [
        { path: "/admin", label: "Dashboard", icon: <LayoutDashboard size={16} /> },
        { path: "/admin/users", label: "Users", icon: <Users size={16} /> },
        { path: "/admin/analytics", label: "Analytics", icon: <BarChart3 size={16} /> },
      ],
    },
    {
      section: "Management",
      items: [
        { path: "/admin/subscriptions", label: "Subscriptions", icon: <Wallet size={16} /> },
        { path: "/admin/subscribers", label: "Subscribers", icon: <Mail size={16} /> },
        { path: "/admin/blog", label: "Blog", icon: <FileText size={16} /> },
        { path: "/admin/notifications", label: "Notifications", icon: <Bell size={16} /> },
      ],
    },
    {
      section: "Marketing Engine",
      items: [
        { path: "/admin/articles", label: "Articles", icon: <FileText size={16} /> },
        { path: "/admin/content", label: "Generated Content", icon: <Copy size={16} /> },
        { path: "/admin/calendar", label: "Posting Calendar", icon: <Clock size={16} /> },
        { path: "/admin/strategy", label: "Content Strategy", icon: <Sparkles size={16} /> },
        { path: "/admin/connections", label: "Connected Accounts", icon: <Link2 size={16} /> },
        { path: "/admin/campaigns", label: "Campaigns", icon: <Mail size={16} /> },
        { path: "/admin/marketing-analytics", label: "Marketing Analytics", icon: <Megaphone size={16} /> },
        { path: "/admin/errors", label: "Error Logs", icon: <AlertCircle size={16} /> },
      ],
    },
    {
      section: "System",
      items: [
        { path: "/admin/system-settings", label: "System Settings", icon: <Power size={16} /> },
        { path: "/admin/security", label: "Security", icon: <ShieldCheck size={16} /> },
        { path: "/admin/settings", label: "Settings", icon: <Settings size={16} /> },
      ],
    },
  ];

  const handleViewSite = () => window.open("/", "_blank");
  const handleViewBlog = () => window.open("/blog", "_blank");

  const getInitials = (name: string): string =>
    name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);

  const getRoleBadgeClass = (role: string): string => {
    switch (role) {
      case "super_admin": return "bg-red/25 text-red border border-red/40";
      case "admin": return "bg-primary/25 text-primary border border-primary/40";
      case "support": return "bg-orange/25 text-orange border border-orange/40";
      case "read_only": return "bg-white/15 text-white/70 border border-white/20";
      default: return "bg-primary/25 text-primary border border-primary/40";
    }
  };

  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm md:hidden"
          onClick={onMobileClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 z-50 h-full w-64 flex flex-col
          bg-[#111113]/95 backdrop-blur-2xl
          border-r border-white/[0.06]
          shadow-[4px_0_24px_rgba(0,0,0,0.4)]
          transition-transform duration-300 ease-in-out
          ${mobileOpen ? "translate-x-0" : "-translate-x-full"}
          md:translate-x-0 md:static md:z-auto md:h-screen
        `}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.06]">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-primary/20 border border-primary/30 flex items-center justify-center">
              <BarChart3 size={16} className="text-primary" />
            </div>
            <div>
              <p className="text-sm font-semibold text-white leading-tight">WaveBudget</p>
              <span className="text-[10px] font-bold tracking-widest text-primary uppercase">Admin</span>
            </div>
          </div>
          <button
            className="md:hidden text-white/40 hover:text-white transition-colors p-1"
            onClick={onMobileClose}
          >
            <X size={16} />
          </button>
        </div>

        {/* Admin Info */}
        <div className="flex flex-col items-center px-5 py-5 border-b border-white/[0.06]">
          <div className="w-12 h-12 rounded-full bg-primary/20 border border-primary/30 flex items-center justify-center text-primary font-bold text-sm mb-2.5">
            {getInitials(admin.name)}
          </div>
          <p className="text-sm font-semibold text-white mb-1.5">{admin.name}</p>
          <span className={`text-[10px] font-bold tracking-wider uppercase px-2.5 py-0.5 rounded-full ${getRoleBadgeClass(admin.role)}`}>
            {admin.role.replace("_", " ")}
          </span>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-0.5">
          {navItems.map((section, idx) => (
            <div key={idx} className="mb-3">
              <p className="px-3 mb-1.5 text-[10px] font-semibold tracking-widest text-white/30 uppercase">
                {section.section}
              </p>
              {section.items.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.path === "/admin"}
                  onClick={onMobileClose}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150 ${
                      isActive
                        ? "bg-primary/15 text-primary border border-primary/20"
                        : "text-white/50 hover:text-white/80 hover:bg-white/[0.04]"
                    }`
                  }
                >
                  <span className="opacity-80">{item.icon}</span>
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        {/* Quick Actions */}
        <div className="px-3 py-3 border-t border-white/[0.06] space-y-1.5">
          <p className="px-3 mb-1.5 text-[10px] font-semibold tracking-widest text-white/30 uppercase">
            Quick Actions
          </p>
          <button
            onClick={handleViewSite}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-white/50 hover:text-white/80 hover:bg-white/[0.04] transition-all duration-150"
          >
            <Globe size={16} />
            View Site
          </button>
          <button
            onClick={handleViewBlog}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-white/50 hover:text-white/80 hover:bg-white/[0.04] transition-all duration-150"
          >
            <FileText size={16} />
            View Blog
          </button>
        </div>

        {/* Logout */}
        <div className="px-3 pb-4">
          <button
            onClick={onLogout}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium text-red hover:text-red hover:bg-red/10 transition-all duration-150"
          >
            <LogOut size={16} />
            Logout
          </button>
        </div>
      </aside>
    </>
  );
}

export default AdminSidebar;
